/**
 * Notification Service
 *
 * Provides browser push notifications for AI responses.
 * Designed for mobile-first experience - notifies user when response is ready.
 */

export class NotificationService {
  private static instance: NotificationService;
  private enabled = false;
  private permission: NotificationPermission = 'default';

  private constructor() {
    this.permission = this.getPermission();
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Get current notification permission status
   */
  private getPermission(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (this.permission === 'granted') {
      this.enabled = true;
      return true;
    }

    try {
      const result = await Notification.requestPermission();
      this.permission = result;
      this.enabled = result === 'granted';
      return this.enabled;
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Show notification when AI response is ready
   */
  showResponseNotification(title: string = 'Space Antics', body: string = 'Your response is ready'): void {
    if (!this.enabled || this.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.svg',
        badge: '/favicon.svg',
        tag: 'space-antics-response',
        requireInteraction: false,
        silent: false,
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  /**
   * Enable notifications (after permission is granted)
   */
  enable(): void {
    if (this.permission === 'granted') {
      this.enabled = true;
    }
  }

  /**
   * Disable notifications
   */
  disable(): void {
    this.enabled = false;
  }
}

export const notificationService = NotificationService.getInstance();
